import React, { useState, useEffect, useRef } from 'react';
import { FormattedMessage } from '../../util/reactIntl';
import { ListingCard } from '../../components';
import css from './SimilarListingsCarousel.module.css';

const SimilarListingsCarousel = ({ similarListings, config, title, fromEvent, keyword }) => {
  const [itemsPerPage, setItemsPerPage] = useState(4);
  const [isMobile, setIsMobile] = useState(false);
  const [error, setError] = useState(null);
  const carouselRef = useRef(null);
  const isDragging = useRef(false);
  const startX = useRef(0);
  const scrollLeft = useRef(0);
  const startY = useRef(0);

  useEffect(() => {
    const handleResize = () => {
      try {
        const mobile = window.innerWidth <= 768;
        setIsMobile(mobile);
        setItemsPerPage(mobile ? 2.5 : 6);
      } catch (err) {
        console.error('Error handling resize:', err);
        setError('Failed to adjust layout for screen size');
      }
    };

    try {
      handleResize();
      window.addEventListener('resize', handleResize);
    } catch (err) {
      console.error('Error setting up resize listener:', err);
      setError('Failed to set up responsive behavior');
    }

    return () => {
      try {
        window.removeEventListener('resize', handleResize);
      } catch (err) {
        console.error('Error cleaning up resize listener:', err);
      }
    };
  }, []);

  if (!similarListings || similarListings.length === 0) {
    return null;
  }

  if (error) {
    return (
      <div className={css.errorContainer}>
        <FormattedMessage
          id="SimilarListingsCarousel.error"
          defaultMessage="Error loading similar listings"
        />
      </div>
    );
  }

  const handlePrevClick = () => {
    try {
      if (carouselRef.current) {
        const slideWidth = carouselRef.current.offsetWidth;
        carouselRef.current.scrollBy({ left: -slideWidth, behavior: 'smooth' });
      }
    } catch (err) {
      console.error('Error handling previous click:', err);
      setError('Failed to navigate carousel');
    }
  };

  const handleNextClick = () => {
    try {
      if (carouselRef.current) {
        const slideWidth = carouselRef.current.offsetWidth;
        carouselRef.current.scrollBy({ left: slideWidth, behavior: 'smooth' });
      }
    } catch (err) {
      console.error('Error handling next click:', err);
      setError('Failed to navigate carousel');
    }
  };

  const handleDragStart = e => {
    try {
      if (isMobile || !carouselRef.current) return;
      isDragging.current = true;
      startX.current = e.type.includes('mouse') ? e.pageX : e.touches[0].clientX;
      startY.current = e.type.includes('mouse') ? e.pageY : e.touches[0].clientY;
      scrollLeft.current = carouselRef.current.scrollLeft;
    } catch (err) {
      console.error('Error starting drag:', err);
      isDragging.current = false;
    }
  };

  const handleDragEnd = () => {
    isDragging.current = false;
  };

  const handleDragMove = e => {
    try {
      if (!isDragging.current || isMobile || !carouselRef.current) return;

      const x = e.type.includes('mouse') ? e.pageX : e.touches[0].clientX;
      const y = e.type.includes('mouse') ? e.pageY : e.touches[0].clientY;

      const walkX = (x - startX.current) * 2;
      const walkY = y - startY.current;

      if (Math.abs(walkY) > Math.abs(walkX)) {
        isDragging.current = false;
        return;
      }

      e.preventDefault();
      carouselRef.current.scrollLeft = scrollLeft.current - walkX;
    } catch (err) {
      console.error('Error during drag movement:', err);
      isDragging.current = false;
    }
  };

  // Safe keyword encoding
  const getKeywordUrl = () => {
    try {
      if (!keyword) return '/s';
      const safeKeyword = typeof keyword === 'string' ? keyword : String(keyword);
      return `/s?keywords=${encodeURIComponent(safeKeyword.toLowerCase().trim())}`;
    } catch (err) {
      console.error('Error processing keyword:', err);
      return '/s';
    }
  };

  return (
    <div className={css.similarListingsSection}>
      <div className={css.titleContainer}>
        <h2 className={fromEvent ? css.similarListingsTitleEvent : css.similarListingsTitle}>
          {fromEvent ? title : <FormattedMessage id={title} />}
        </h2>
        {fromEvent && (
          <a href={getKeywordUrl()} className={css.seeMoreLink}>
            <FormattedMessage id="SimilarListingsCarousel.seeMore" defaultMessage="See More" />
          </a>
        )}
      </div>
      <div className={css.carouselContainer}>
        {!isMobile && (
          <button className={css.carouselButton} onClick={handlePrevClick}>
            &lt;
          </button>
        )}
        <div
          className={css.carouselWrapper}
          ref={carouselRef}
          onMouseDown={handleDragStart}
          onMouseLeave={handleDragEnd}
          onMouseUp={handleDragEnd}
          onMouseMove={handleDragMove}
          onTouchStart={handleDragStart}
          onTouchEnd={handleDragEnd}
          onTouchMove={handleDragMove}
        >
          <div className={css.carouselTrack}>
            {similarListings.map(listing => {
              try {
                if (!listing?.id?.uuid) {
                  console.warn('Listing missing ID', listing);
                  return null;
                }
                return (
                  <div
                    key={listing.id.uuid}
                    className={css.carouselItem}
                    style={{
                      flex: `0 0 ${100 / itemsPerPage}%`,
                      maxWidth: `${100 / itemsPerPage}%`,
                      width: `${100 / itemsPerPage}%`,
                    }}
                  >
                    <ListingCard
                      listing={listing}
                      showAuthorInfo={true}
                      config={config}
                      isShort={true}
                    />
                  </div>
                );
              } catch (err) {
                console.error('Error rendering listing card:', err);
                return null;
              }
            })}
          </div>
        </div>
        {!isMobile && (
          <button className={css.carouselButton} onClick={handleNextClick}>
            &gt;
          </button>
        )}
      </div>
    </div>
  );
};

export default SimilarListingsCarousel;
